var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { unsafeCSS, css, html, svg, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
// Import styles
import styles from './web-component-style.scss?inline';
/**
 * `<uo-hub-spoke-graph>`
 * A hub-and-spoke radial graph.
 * Renders a central node with any number of peripheral nodes evenly spaced around it, connected by lines.
 */
let AutomatorHubSpokeGraph = class AutomatorHubSpokeGraph extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Content for the center node. Must be a Lit `TemplateResult`.
         * This prevents accidental injection of raw HTML or Elements.
         * @type {TemplateResult}
         * @default html``
         */
        this.centerItem = html ``;
        /**
         * Array of contents for each peripheral node.
         * Each item must be a Lit `TemplateResult`.
         * @type {Array<TemplateResult>}
         * @default []
         */
        this.peripheralItems = [];
        /**
         * Distance in pixels from the center of the graph to the center of each peripheral node.
         * @type {number}
         * @default 105
         */
        this.radius = 105;
        /**
         * Diameter in pixels of every node circle (center + peripherals).
         * @type {number}
         * @default 60
         */
        this.nodeDiameter = 60;
        /**
         * Thickness in pixels of the lines connecting center to peripherals.
         * @type {number}
         * @default 2
         */
        this.lineWidth = 5;
        /**
         * Color of the connecting lines. Should be a valid CSS color string.
         * @type {string}
         * @default '#f5f7fb'
         */
        this.lineColor = '#f5f7fb';
    }
    /**
     * Renders the component's DOM structure based on its properties and slotted content.
     * @return {TemplateResult} The HTML template for the hub-spoke graph component.
     */
    render() {
        // 1) Compute shared values once
        const count = Math.max(this.peripheralItems.length, 1);
        const size = this.calculateContainerSize();
        const center = this.calculateCenterCoord(size);
        const step = this.calculateAngleStep(count);
        const rHalf = this.nodeDiameter / 2;
        return html `
			<div class="content">
				<div class="graph" style="width:${size}px; height:${size}px">
					${this.renderLines(center, step)}
					${this.renderPeripheralNodes(center, step, rHalf)}
					${this.renderCenterNode(center, rHalf)}
				</div>
			</div>
		`;
    }
    /**
     * Calculate the total width/height of the container:
     * = (radius out) + nodeRadius + (radius out) + nodeRadius
     * @return {number} total container size in pixels
     */
    calculateContainerSize() {
        return this.radius * 2 + this.nodeDiameter;
    }
    /**
     * Compute the coordinate of the container’s center.
     * @param {number} size Container width/height (px)
     * @return {number} pixel coordinate of center
     */
    calculateCenterCoord(size) {
        return size / 2;
    }
    /**
     * Compute the angular step between each spoke in radians.
     * @param {number} count number of peripheral nodes
     * @return {number} angle step
     */
    calculateAngleStep(count) {
        return (2 * Math.PI) / count;
    }
    /**
     * Given an (index), compute the x/y position (center) of that peripheral node.
     * @param {number} index zero-based index of the node
     * @param {number} center pixel coordinate of graph center
     * @param {number} step angular step between nodes
     * @return {Object} x/y position of the node
     */
    calculateNodePosition(index, center, step) {
        const angle = index * step - Math.PI / 2; // start at “12 o’clock”
        return {
            x: center + this.radius * Math.cos(angle),
            y: center + this.radius * Math.sin(angle),
        };
    }
    /**
     * Render the `<svg>` and all `<line>`s connecting center to peripherals.
     * @param {number} center pixel coordinate of graph center
     * @param {number} step angular step between nodes
     * @return {SVGTemplateResult} SVG template result
     */
    renderLines(center, step) {
        const size = this.calculateContainerSize();
        return svg `
			<svg class="lines" width="${size}" height="${size}">
				${this.peripheralItems.map((_, i) => {
            const { x, y } = this.calculateNodePosition(i, center, step);
            return svg `
						<line
							x1="${center}" y1="${center}"
							x2="${x}"      y2="${y}"
							stroke="${this.lineColor}"
							stroke-width="${this.lineWidth}"
						></line>
					`;
        })}
			</svg>
		`;
    }
    /**
     * Render each peripheral `.node`, absolutely positioned.
     * @param {number} center pixel coordinate of graph center
     * @param {number} step angular step between nodes
     * @param {number} rHalf half of the node diameter
     * @return {TemplateResult[]} Template result array
     */
    renderPeripheralNodes(center, step, rHalf) {
        return this.peripheralItems.map((item, i) => {
            const pos = this.calculateNodePosition(i, center, step);
            return html `
			<div
				class="node peripheral"
				style="
					width:${this.nodeDiameter}px;
					height:${this.nodeDiameter}px;
					top:${pos.y - rHalf}px;
					left:${pos.x - rHalf}px;
				"
			>
				${item}
			</div>
		`;
        });
    }
    /**
     * Render the single center `.node`, absolutely positioned in the middle.
     * @param {number} center pixel coordinate of graph center
     * @param {number} rHalf half of the node diameter
     * @return {TemplateResult} Template result
     */
    renderCenterNode(center, rHalf) {
        return html `
			<div
				class="node center"
				style="
				width:${this.nodeDiameter}px;
				height:${this.nodeDiameter}px;
				top:${center - rHalf}px;
				left:${center - rHalf}px;
				"
			>
				${this.centerItem}
			</div>
		`;
    }
};
AutomatorHubSpokeGraph.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: Object })
], AutomatorHubSpokeGraph.prototype, "centerItem", void 0);
__decorate([
    property({ type: Array })
], AutomatorHubSpokeGraph.prototype, "peripheralItems", void 0);
__decorate([
    property({ type: Number })
], AutomatorHubSpokeGraph.prototype, "radius", void 0);
__decorate([
    property({ type: Number })
], AutomatorHubSpokeGraph.prototype, "nodeDiameter", void 0);
__decorate([
    property({ type: Number })
], AutomatorHubSpokeGraph.prototype, "lineWidth", void 0);
__decorate([
    property({ type: String })
], AutomatorHubSpokeGraph.prototype, "lineColor", void 0);
AutomatorHubSpokeGraph = __decorate([
    customElement('uo-hub-spoke-graph')
], AutomatorHubSpokeGraph);
export { AutomatorHubSpokeGraph };
